import { renderProductCardFromElastic } from '../ProductCard';

export const listingElasticMethods = {
    canUseElastic() {
        return Boolean(this.bootstrap.elasticApiUrl);
    },

    getContextTermSlug() {
        const datasetSlug = String(this.root?.dataset?.termSlug || '').trim();

        if (datasetSlug) {
            return datasetSlug;
        }

        const bootstrapSlug = String(this.bootstrap.termSlug || '').trim();

        if (bootstrapSlug) {
            return bootstrapSlug;
        }

        const taxonomy = String(
            this.state.taxonomy || this.bootstrap.primaryTaxonomy || ''
        ).trim();
        const termId = Number(this.state.termId || this.bootstrap.termId || 0);

        if (!taxonomy || termId <= 0) {
            return '';
        }

        const termIndex = this.bootstrap.termIndex?.[taxonomy] || {};

        return (
            Object.values(termIndex).find(
                term => Number(term?.id || 0) === termId
            )?.slug || ''
        );
    },

    shouldRefetchAfterSsr(state = this.state) {
        const current = {
            page: Number(state.page || 1),
            sort: state.sort || 'popular',
            filters: state.filters || {},
            phrase: String(state.phrase || '').trim(),
        };
        const baseline = {
            page: Number(this.ssrState.page || 1),
            sort: this.ssrState.sort || 'popular',
            filters: this.ssrState.filters || {},
            phrase: String(
                this.ssrState.phrase || this.getPhraseFromBootstrap()
            ).trim(),
        };

        if (current.page !== baseline.page) {
            return true;
        }

        if ((current.sort || 'popular') !== (baseline.sort || 'popular')) {
            return true;
        }

        if (this.isSearchMode() && current.phrase !== baseline.phrase) {
            return true;
        }

        if (this.isSearchMode()) {
            return false;
        }

        return (
            JSON.stringify(this.normalizeFilters(current.filters)) !==
            JSON.stringify(this.normalizeFilters(baseline.filters))
        );
    },

    async fetchListing() {
        return this.fetchListingPayload(this.state);
    },

    async fetchListingCount(listingState = this.state) {
        if (!this.canUseElastic()) {
            return 0;
        }

        const elasticData = await this.fetchElasticListingRaw(listingState);

        return Number(elasticData.totalHits || 0);
    },

    async fetchListingPayload(listingState = this.state) {
        if (!this.canUseElastic()) {
            throw new Error(
                'Listing wymaga skonfigurowanego ELASTIC_NODE_API.'
            );
        }

        return this.fetchElasticListingPayload(listingState);
    },

    logElasticRequestOnce(elasticBody, listingState = this.state) {
        if (!this.bootstrap.debugElasticRequest || this.elasticRequestLogged) {
            return;
        }

        this.elasticRequestLogged = true;

        console.info('[mda-listing-es]', {
            source: 'js-client',
            page_url: this.bootstrap.pageUrl || window.location.href,
            request_uri: window.location.pathname + window.location.search,
            elastic_api_url: this.bootstrap.elasticApiUrl || '',
            bootstrap: {
                taxonomy: this.bootstrap.taxonomy || '',
                primaryTaxonomy: this.bootstrap.primaryTaxonomy || '',
                termId: this.bootstrap.termId || 0,
                termSlug: this.bootstrap.termSlug || '',
                isVirtualTerm: Boolean(this.bootstrap.isVirtualTerm),
                contextTermSlug: this.getContextTermSlug(),
            },
            listing_state: {
                taxonomy: listingState.taxonomy || '',
                termId: listingState.termId || 0,
                page: listingState.page || 1,
                sort: listingState.sort || 'popular',
                filters: listingState.filters || {},
            },
            elastic_body: elasticBody,
        });
    },

    async fetchElasticListingRaw(listingState = this.state) {
        const elasticBody = this.buildElasticRequestBody(listingState);
        this.logElasticRequestOnce(elasticBody, listingState);
        const elasticHeaders = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };

        if (this.bootstrap.elasticApiToken) {
            elasticHeaders['X-Listing-Token'] = this.bootstrap.elasticApiToken;
        }

        const elasticResponse = await fetch(this.bootstrap.elasticApiUrl, {
            method: 'POST',
            headers: elasticHeaders,
            body: JSON.stringify(elasticBody),
        });

        if (!elasticResponse.ok) {
            throw new Error('Elastic listing request failed.');
        }

        const elasticData = await elasticResponse.json();

        if (!elasticData?.success) {
            throw new Error('Elastic listing response was not successful.');
        }

        return elasticData;
    },

    async fetchElasticListingPayload(listingState = this.state) {
        const elasticData = await this.fetchElasticListingRaw(listingState);
        const perPage = Number(
            elasticData.perPage || this.bootstrap.perPage || 16
        );
        const page = Number(elasticData.page || listingState.page || 1);
        const total = Number(elasticData.totalHits || 0);
        const maxPages = Math.max(
            Number(elasticData.totalPages || 0),
            total > 0 ? Math.ceil(total / perPage) : 1
        );
        const current = Math.min(total, page * perPage);
        const hasMore = page < maxPages;

        const products = Array.isArray(elasticData.products)
            ? elasticData.products
            : [];
        const results = products.map(product => {
            const id = Number(product.id || product.post_id || 0);

            return {
                id,
                html: renderProductCardFromElastic(product, {
                    placeholderImageUrl:
                        this.bootstrap.placeholderImageUrl || '',
                }),
            };
        });

        const payload = {
            results,
            total,
            pagination: {
                page,
                perPage,
                current,
                total,
                maxPages,
                hasMore,
            },
            activeFilterChips: this.buildActiveFilterChipsFromState(
                listingState.filters
            ),
            sort: listingState.sort,
            page,
        };

        if (this.isSearchMode()) {
            payload.title = this.formatSearchTitle(
                String(listingState.phrase || this.getPhraseFromBootstrap())
            );
        }

        return payload;
    },

    formatSearchTitle(phrase) {
        const normalizedPhrase = String(phrase || '').trim();

        if (!normalizedPhrase || !this.title) {
            return normalizedPhrase;
        }

        const currentTitle = String(this.title.textContent || '').trim();

        if (currentTitle.includes(normalizedPhrase)) {
            return currentTitle;
        }

        return normalizedPhrase;
    },

    getFilterTaxonomies() {
        const fromBootstrap = this.bootstrap.filterTaxonomies;

        if (Array.isArray(fromBootstrap) && fromBootstrap.length > 0) {
            return fromBootstrap;
        }

        return ['product_cat', 'collection', 'room', 'style'];
    },

    buildElasticRequestBody(listingState = this.state) {
        if (this.isSearchMode()) {
            const phrase = String(
                listingState.phrase || this.getPhraseFromBootstrap()
            ).trim();

            return {
                listing: true,
                page: listingState.page,
                perPage: this.bootstrap.perPage || 16,
                sortBy: this.mapSortToElastic(listingState.sort),
                language: this.bootstrap.language || 'pl',
                phrase,
                contextTaxonomy: '',
                contextTermSlug: '',
                filters: {},
                filterTermIds: {},
            };
        }

        const taxonomies = this.getFilterTaxonomies();
        const primaryTaxonomy =
            this.bootstrap.primaryTaxonomy || listingState.taxonomy || '';
        const contextTermSlug = this.getContextTermSlug();
        const requestFilters = {};
        const filterTermIds = {};

        taxonomies.forEach(taxonomy => {
            const selectedSlugs = listingState.filters?.[taxonomy];
            const hasExplicitFilterKey = Object.prototype.hasOwnProperty.call(
                listingState.filters || {},
                taxonomy
            );
            let slugs = [];

            if (Array.isArray(selectedSlugs)) {
                if (selectedSlugs.length > 0) {
                    slugs = this.expandTermSlugs(taxonomy, selectedSlugs);
                } else if (hasExplicitFilterKey) {
                    slugs = [];
                }
            } else if (selectedSlugs) {
                slugs = this.expandTermSlugs(taxonomy, [selectedSlugs]);
            } else if (
                !hasExplicitFilterKey &&
                taxonomy === primaryTaxonomy &&
                contextTermSlug &&
                !this.bootstrap.isVirtualTerm
            ) {
                slugs = this.expandTermSlugs(taxonomy, [contextTermSlug]);
            }

            if (slugs.length > 0) {
                requestFilters[taxonomy] = slugs;
                const termIds = this.expandTermIds(taxonomy, slugs);

                if (termIds.length > 0) {
                    filterTermIds[taxonomy] = termIds;
                }
            }
        });

        return {
            listing: true,
            page: listingState.page,
            perPage: this.bootstrap.perPage || 16,
            sortBy: this.mapSortToElastic(listingState.sort),
            language: this.bootstrap.language || 'pl',
            contextTaxonomy: primaryTaxonomy,
            contextTermSlug:
                !this.bootstrap.isVirtualTerm && contextTermSlug
                    ? String(contextTermSlug)
                    : '',
            filters: requestFilters,
            filterTermIds,
        };
    },

    expandTermIds(taxonomy, slugs = []) {
        const termIndex = this.bootstrap.termIndex?.[taxonomy] || {};
        const ids = new Set();

        this.expandTermSlugs(taxonomy, slugs).forEach(slug => {
            const termId = Number(termIndex[slug]?.id || 0);

            if (termId > 0) {
                ids.add(termId);
            }
        });

        return Array.from(ids);
    },

    expandTermSlugs(taxonomy, slugs = []) {
        const descendantsMap = this.bootstrap.termDescendants?.[taxonomy] || {};
        const expanded = new Set();

        slugs.forEach(slug => {
            const normalizedSlug = String(slug || '').trim();

            if (!normalizedSlug) {
                return;
            }

            const branch = descendantsMap[normalizedSlug];

            if (Array.isArray(branch) && branch.length > 0) {
                branch.forEach(item => expanded.add(item));
                return;
            }

            expanded.add(normalizedSlug);
        });

        return Array.from(expanded);
    },

    mapSortToElastic(sort) {
        switch (sort) {
            case 'bestseller':
                return 'popularity';
            case 'newest':
                return 'newest';
            case 'price_asc':
            case 'price_desc':
                return sort;
            default:
                return 'popular';
        }
    }
};
